
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { ValidationProvider } from 'vee-validate';
import { getSchoolDeliveryOptions } from '@/utils/helpers';
import MultiSelect from '@/components/shared/MultiSelect.vue';
import 'vue-select/dist/vue-select.css';

@Component<StudentFormFields>({
	components: {
		ValidationProvider,
		MultiSelect
	}
})
export default class StudentFormFields extends Vue {
	@Prop({ required: true }) private student!: Partial<UserStudent>;
	@Prop({ required: true }) private restaurant!: Restaurant;
	@Prop({ required: true }) private groupLocations!: GroupLocation[];
	@Prop({ default: null }) private deliveryInfo!: SchoolDelivery | null;
	@Prop({ type: Boolean, default: false }) private lockLocation!: boolean;
	@Watch('deliveryInfo', { immediate: true, deep: true })
	private onDeliveryInfoChange(): void {
		if (this.siteOptions.length === 1 && this.student.restaurantId && !this.student.school) {
			this.updateField('school', this.siteOptions[0], {}, false);
		}
	}

	/**
	 * Get site (school) options based on selected location
	 * Only shown when there are multiple schools in the selected location
	 *
	 * @return {string[]}
	 */
	private get siteOptions(): string[] {
		if (!this.deliveryInfo?.schools) {
			return [];
		}
		return this.deliveryInfo.schools.map(school => school.name).filter((name): name is string => typeof name === 'string');
	}

	/**
	 * This getter fetches the school delivery information.
	 * It is used to populate the school's dependant fields
	 *
	 * @return {SchoolDeliveryOptions}
	 */
	private get schoolDeliveryOptions(): SchoolDeliveryOptions {
		if (!this.deliveryInfo) {
			return { grades: null, classrooms: null, teachers: null, extra: null };
		}
		return getSchoolDeliveryOptions(this.deliveryInfo, this.student.school);
	}

	/**
	 * Check if school-dependent fields should be enabled
	 * Fields are enabled when:
	 * - restaurantId is set
	 * - school is defined (even if empty string, for default schools with no name)
	 * - if multiple schools exist, school must be non-empty
	 *
	 * @return {boolean}
	 */
	private get isSchoolSelected(): boolean {
		const hasRestaurant = !!this.student.restaurantId;
		const schoolIsDefined = this.student.school !== undefined;
		const schoolCount = this.deliveryInfo?.schools?.length ?? 0;

		// If only one school, empty string is allowed
		if (schoolCount <= 1) {
			return hasRestaurant && schoolIsDefined;
		}

		// If more than one school, school must not be empty
		const schoolIsNonEmpty = this.student.school !== '';

		return hasRestaurant && schoolIsDefined && schoolIsNonEmpty;
	}

	/**
	 * Get filtered extra options (non-empty items, trimmed)
	 *
	 * @return {string[]}
	 */
	private get filteredExtra(): string[] {
		return this.schoolDeliveryOptions.extra?.filter(item => item?.trim()).map(item => item.trim()) || [];
	}

	/**
	 * Convert extra string to array for the MultiSelect component
	 *
	 * @return {string[]}
	 */
	private get extraArray(): string[] {
		const info = this.student.extra;
		if (!info || typeof info !== 'string') {
			return [];
		}
		return info.split(',').map(item => item.trim()).filter(item => item.length);
	}

	/**
	 * Set restaurantId on create if not already set
	 * For non-group or group with 1 location: use current restaurant
	 *
	 * @return {void}
	 */
	private created(): void {
		if (!this.student.restaurantId) {
			// TODO: Remove the restaurant.group (but keep the '.groups' one) check when locations are ready to use restaurant.groups
			if (this.lockLocation || (!this.restaurant.group && !this.restaurant.groups?.length) || this.groupLocations.length === 1) {
				this.updateField('restaurantId', this.restaurant.app8_restaurant);
				this.updateField('locationSlug', this.restaurant.slug);
				this.handleLocationChange(this.restaurant.app8_restaurant);
			}
		}
	}

	/**
	 * Update a specific field in the student object
	 *
	 * @param {string} field
	 * @param {any} value
	 * @param {Partial<UserStudent>} additionalUpdates
	 * @param {boolean} resetDependentFields - If true, reset dependent fields when school changes
	 * @return {void}
	 */
	private updateField(field: string, value: any, additionalUpdates: Partial<UserStudent> = {}, resetDependentFields: boolean = true): void {
		const updatedStudent = { ...this.student, [field]: value, ...additionalUpdates };

		// Reset dependent fields when school changes (only if explicitly requested)
		if (field === 'school' && resetDependentFields) {
			updatedStudent.grade = '';
			updatedStudent.classroom = '';
			updatedStudent.teacher = '';
			updatedStudent.extra = '';
		}

		this.$emit('update:student', updatedStudent);
	}

	/**
	 * Update extra from array
	 *
	 * @param {string[]} value
	 * @return {void}
	 */
	private updateExtraArray(value: string[]): void {
		this.updateField('extra', value.join(','));
	}

	/**
	 * Handle location change
	 * Resets dependent fields and emits event for parent to update location data
	 *
	 * @param {string} restaurantId
	 * @return {void}
	 */
	private handleLocationChange(restaurantId: string): void {
		const updatedStudent = {
			...this.student,
			school: '',
			grade: '',
			classroom: '',
			teacher: '',
			extra: ''
		};
		this.$emit('update:student', updatedStudent);
		this.$emit('location-change', restaurantId);
	}
}
